/**
 * The app shell: card stack, safe-area handling, and the DOM builders every card is made of
 * (TASKS T43, SPEC §2 `ui/shell.ts`).
 *
 * Two rules shape the whole module.
 *
 *  1. **A card is data, not DOM.** Cards hand the shell a `CardSpec` — a display region, a control
 *     region, an optional notice — and the shell owns mounting, ordering, focus, and announcement.
 *     Cards that build their own layout is exactly how the thumb-zone rule rots one card at a time
 *     (T44 turns that ownership into a structural guarantee).
 *  2. **Nothing readable is ever drawn to the canvas** (BRIEF <stack>). Every string here is DOM
 *     text, so it scales with the OS text size and is selectable, findable, and speakable.
 *
 * The shell holds no game state and makes no decisions. It renders what it is given.
 */

import { Announcer, focusPrimary } from './a11y';
import type { Phase } from '../state/gamestate';

// ---------------------------------------------------------------------------------------------
// Card shapes
// ---------------------------------------------------------------------------------------------

/**
 * A single line of non-blocking explanation, pinned above the card's read-only content.
 *
 * Carries its own test id because the two notices this app has — standalone mode killing speech,
 * and an es-ES-only voice — are separately assertable facts about the platform, not interchangeable
 * decoration.
 */
export interface Notice {
  message: string;
  testId?: string;
}

export interface CardSpec {
  /**
   * Repaint key. The shell rebuilds only when the whole stack's keys change, so a 60Hz update loop
   * does not thrash the DOM — and, more importantly, does not blow away a half-typed answer or
   * dismiss the iOS keyboard sixty times a second.
   */
  key: string;
  /** Marks the layer for tests and for CSS. Not read by the shell for any behaviour. */
  name: string;
  /** Read-only content, upper region. Interactive nodes here are a bug (enforced in T44). */
  display: readonly Node[];
  /** Interactive controls, in tab order. Mounted into the thumb zone. */
  controls: readonly HTMLElement[];
  /** One line, never a modal, never blocking. */
  notice?: Notice | null;
  /** Announced to the polite live region when this card first mounts. */
  announce?: string;
  /**
   * The tier this card belongs to, if any (1-6).
   *
   * Sets `--card-hue`, which the ground, the control deck and the tier mark all read. Purely
   * decorative — every card states in words what it is asking for, so nothing is ever signalled by
   * colour alone. See DECISIONS D-185.
   */
  tier?: number | null;
  /**
   * Where focus belongs after this rebuild, by `data-testid`. Falls back to the primary action when
   * the named control is not there.
   *
   * Exists for cards whose **controls are their state** — the tile card, whose every tap rebuilds
   * it (T50). Without this, placing a tile hands focus to the primary action, and the next Enter
   * submits a half-built sentence instead of placing the next word. A typed card needs none of this
   * and sets nothing: it is not rebuilt while it is being answered, precisely so the field survives.
   */
  focus?: string;
}

/**
 * Persistent run chrome, rendered beside the stack rather than inside a card.
 *
 * The round timer lives here, and it has to: the timer changes once a second, a card is rebuilt
 * whenever its key changes, and a rebuild destroys the answer field — dismissing the iOS keyboard
 * and losing what has been typed. Putting the timer in the card key would do that sixty times a
 * minute; leaving it out of the key would freeze it. So it is not card content at all.
 *
 * Read-only by construction: chrome is never inside `.card__controls`, so anything interactive here
 * would be unreachable by thumb. Nothing may be put here that the learner has to touch.
 */
export interface ChromeSpec {
  key: string;
  nodes: readonly Node[];
}

// ---------------------------------------------------------------------------------------------
// The shell
// ---------------------------------------------------------------------------------------------

export interface ShellOptions {
  doc?: Document;
  /**
   * Called with the top layer's root after every rebuild. T44 installs the thumb-zone assertion
   * here; the effects layer and future instrumentation hang off the same hook rather than each
   * reaching into the shell's internals.
   */
  afterRender?: (top: HTMLElement, spec: CardSpec) => void;
}

export class Shell {
  readonly announcer: Announcer;
  private readonly doc: Document;
  private readonly afterRender: ((top: HTMLElement, spec: CardSpec) => void) | undefined;
  private lastKey = '';
  private announced = '';
  private chromeNode: HTMLElement | null = null;
  private chromeKey = '';

  constructor(
    private readonly root: HTMLElement,
    options: ShellOptions = {},
  ) {
    this.doc = options.doc ?? root.ownerDocument;
    this.afterRender = options.afterRender;
    this.announcer = new Announcer(this.doc);
  }

  /**
   * Render a stack of cards, bottom to top.
   *
   * Only the top layer is interactive. Lower layers stay mounted and visible but are marked `inert`
   * so neither the keyboard nor a screen reader can reach them — which is what lets the feedback
   * card sit over the item it is about (T56) instead of replacing it, so the learner can still see
   * what they typed while reading why it was wrong.
   */
  render(stack: readonly CardSpec[], phase: Phase, chrome: ChromeSpec | null = null): void {
    const key = `${phase}::${stack.map((spec) => spec.key).join('>')}`;
    if (key !== this.lastKey) {
      this.lastKey = key;
      this.rebuild(stack, phase);
    }
    // Always, and after the cards: chrome outlives them and updates on its own key.
    this.renderChrome(chrome);
  }

  private rebuild(stack: readonly CardSpec[], phase: Phase): void {
    this.root.dataset['phase'] = phase;
    this.paintHue(stack[stack.length - 1]?.tier ?? null);
    this.chromeNode = null;
    this.chromeKey = '';

    const nodes = stack.map((spec, index) => this.build(spec, index === stack.length - 1));
    this.root.replaceChildren(...nodes);

    const topSpec = stack[stack.length - 1];
    const topNode = nodes[nodes.length - 1];
    if (!topSpec || !topNode) return;

    this.afterRender?.(topNode, topSpec);
    const requested = topSpec.focus
      ? topNode.querySelector<HTMLElement>(`[data-testid="${topSpec.focus}"]`)
      : null;
    if (requested) requested.focus();
    else focusPrimary(topNode, this.doc);
    if (topSpec.announce && topSpec.announce !== this.announced) {
      this.announced = topSpec.announce;
      this.announcer.announce(topSpec.announce);
    }
  }

  private renderChrome(chrome: ChromeSpec | null): void {
    if (!chrome) {
      this.chromeNode?.remove();
      this.chromeNode = null;
      this.chromeKey = '';
      return;
    }
    if (!this.chromeNode) {
      this.chromeNode = this.el('div', 'chrome');
      this.root.append(this.chromeNode);
      this.chromeKey = '';
    }
    if (chrome.key === this.chromeKey) return;
    this.chromeKey = chrome.key;
    this.chromeNode.replaceChildren(...chrome.nodes);
  }

  /**
   * Publish the current card's hue on the root element.
   *
   * On `documentElement` rather than on the card, because the page's background gradient reads it
   * too and that is painted on `body`. Cleared to the one accent where a card has no tier — the
   * entry and reveal cards belong to no tier and must not inherit the last one's colour.
   */
  private paintHue(tier: number | null): void {
    const root = this.doc.documentElement;
    if (tier === null) root.style.removeProperty('--card-hue');
    else root.style.setProperty('--card-hue', `var(--tier${tier})`);
  }

  /** Force the next `render` to rebuild even if the keys match. Used after a reset. */
  invalidate(): void {
    this.lastKey = '';
    this.announced = '';
    this.chromeNode = null;
    this.chromeKey = '';
  }

  private build(spec: CardSpec, interactive: boolean): HTMLElement {
    const card = this.el('section', 'card');
    card.dataset['card'] = spec.name;
    if (!interactive) {
      card.setAttribute('inert', '');
      card.setAttribute('aria-hidden', 'true');
      card.dataset['layer'] = 'behind';
    }

    const display = this.el('div', 'card__display');
    if (spec.notice) display.append(this.notice(spec.notice));
    display.append(...spec.display);

    /*
     * The controls go inside a `deck` within the region, not straight into it.
     *
     * The region is a full grid row — the whole lower 60% — and painting the surface on *that* left
     * a large empty panel under a card with two buttons on it. The deck sizes to its content and is
     * pushed to the bottom, so the raised surface is exactly as tall as there is something to touch.
     * Anything interactive is still inside `.card__controls`, which is what T44's guard tests.
     */
    const controls = this.el('div', 'card__controls');
    const deck = this.el('div', 'deck');
    deck.append(...spec.controls);
    controls.append(deck);

    card.append(display, controls);
    return card;
  }

  private notice(spec: Notice): HTMLElement {
    const node = this.el('p', 'notice');
    node.textContent = spec.message;
    node.dataset['testid'] = spec.testId ?? 'notice';
    return node;
  }

  private el(tag: string, className: string): HTMLElement {
    const node = this.doc.createElement(tag);
    node.className = className;
    return node;
  }
}

// ---------------------------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------------------------

/**
 * These exist so cards never touch `createElement` directly. That is not tidiness: `button()` is
 * the only place a control gets its 56px minimum target and its `type="button"`, and a card that
 * hand-rolled one would silently ship a submit button that reloads the page on Enter.
 */

export function el(tag: string, className?: string, doc: Document = document): HTMLElement {
  const node = doc.createElement(tag);
  if (className) node.className = className;
  return node;
}

export function text(
  tag: string,
  className: string,
  content: string,
  testId?: string,
  doc: Document = document,
): HTMLElement {
  const node = el(tag, className, doc);
  node.textContent = content;
  if (testId) node.dataset['testid'] = testId;
  return node;
}

export function headline(content: string, testId?: string, doc?: Document): HTMLElement {
  return text('h1', 'headline', content, testId, doc);
}

export function meta(content: string, testId?: string, doc?: Document): HTMLElement {
  return text('p', 'meta', content, testId, doc);
}

/** The Spanish or English an item is asking about. Larger, never dimmed. */
export function prompt(content: string, testId?: string, doc?: Document): HTMLElement {
  return text('p', 'prompt', content, testId, doc);
}

export interface ButtonSpec {
  label: string;
  testId: string;
  onClick: () => void;
  /** Bottom-right, filled. Exactly one per card. */
  primary?: boolean;
  /** Visually quieter. Still a full-size target — quiet is not smaller. */
  quiet?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}

export function button(spec: ButtonSpec, doc: Document = document): HTMLButtonElement {
  const node = doc.createElement('button');
  // Never a submit: there is no form, and a submit button reloads the page on Enter.
  node.type = 'button';
  node.textContent = spec.label;
  node.dataset['testid'] = spec.testId;
  if (spec.primary) node.dataset['primary'] = 'true';
  if (spec.quiet) node.dataset['quiet'] = 'true';
  if (spec.disabled) node.disabled = true;
  if (spec.ariaLabel) node.setAttribute('aria-label', spec.ariaLabel);
  node.addEventListener('click', spec.onClick);
  return node;
}

export interface NumberFieldSpec {
  label: string;
  testId: string;
  /** Rendered value. The card owns the draft; this builder holds no state. */
  value: string;
  /** Shown inside the empty field. Says what blank *means*, never an example number. */
  placeholder?: string;
  min?: number;
  max?: number;
  onInput: (value: string) => void;
}

/**
 * A labelled numeric field.
 *
 * `type="number"` with `inputmode="numeric"` because iOS picks the keypad from both and disagrees
 * about which wins across versions; the cost of getting it wrong is the full alphabetic keyboard
 * covering the card while one thumb hunts for a digit.
 *
 * The `<label>` wraps the input rather than pointing at it by `id`. Cards are rebuilt on every key
 * change, so any id would have to be unique per mount or two mounted cards would share one — and a
 * label pointing at the wrong field is worse than no label.
 */
export function numberField(spec: NumberFieldSpec, doc: Document = document): HTMLElement {
  const wrapper = el('label', 'field', doc);
  wrapper.append(text('span', 'field__label', spec.label, `${spec.testId}-label`, doc));

  const input = doc.createElement('input');
  input.type = 'number';
  input.className = 'field__input';
  input.inputMode = 'numeric';
  input.autocomplete = 'off';
  input.enterKeyHint = 'done';
  input.value = spec.value;
  input.dataset['testid'] = spec.testId;
  if (spec.placeholder) input.placeholder = spec.placeholder;
  if (spec.min !== undefined) input.min = String(spec.min);
  if (spec.max !== undefined) input.max = String(spec.max);
  input.addEventListener('input', () => spec.onInput(input.value));
  wrapper.append(input);

  return wrapper;
}

export interface TextFieldSpec {
  label: string;
  testId: string;
  value: string;
  placeholder?: string;
  onInput: (value: string) => void;
}

/**
 * The answer field.
 *
 * `autocapitalize`, `autocorrect` and `spellcheck` are all off. iOS would otherwise capitalise the
 * first letter, "correct" Spanish into English, and underline every word in red — turning a typed
 * production task into a fight with the keyboard, and, worse, silently fixing the learner's answer
 * before the validator ever sees it.
 *
 * `lang="es"` so the OS knows what is being typed, and so a screen reader reads it as Spanish.
 * The accent bar that belongs above this field arrives in T48.
 */
export function textField(spec: TextFieldSpec, doc: Document = document): HTMLElement {
  const wrapper = el('label', 'field', doc);
  wrapper.append(text('span', 'field__label', spec.label, `${spec.testId}-label`, doc));

  const input = doc.createElement('input');
  input.type = 'text';
  input.className = 'field__input';
  input.lang = 'es';
  input.autocapitalize = 'off';
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.setAttribute('autocorrect', 'off');
  input.enterKeyHint = 'send';
  input.value = spec.value;
  input.dataset['testid'] = spec.testId;
  if (spec.placeholder) input.placeholder = spec.placeholder;
  input.addEventListener('input', () => spec.onInput(input.value));
  wrapper.append(input);

  return wrapper;
}

/**
 * A multi-line field.
 *
 * Exists for exactly one thing: the repeat errors written down after a conversation, which are "one
 * per line" and therefore cannot be typed into an `<input>` at all — a single-line field silently
 * drops the newline, so the second error was being swallowed by the first. Same suppressions as
 * `textField`: iOS must not capitalise, correct, or underline the Spanish before it is read.
 */
export function textArea(spec: TextFieldSpec & { rows?: number }, doc: Document = document): HTMLElement {
  const wrapper = el('label', 'field', doc);
  wrapper.append(text('span', 'field__label', spec.label, `${spec.testId}-label`, doc));

  const input = doc.createElement('textarea');
  input.className = 'field__input field__input--multiline';
  input.rows = spec.rows ?? 3;
  input.lang = 'es';
  input.autocapitalize = 'off';
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.setAttribute('autocorrect', 'off');
  input.value = spec.value;
  input.dataset['testid'] = spec.testId;
  if (spec.placeholder) input.placeholder = spec.placeholder;
  input.addEventListener('input', () => spec.onInput(input.value));
  wrapper.append(input);

  return wrapper;
}

/**
 * A row of controls, laid out left to right.
 *
 * Order is meaningful and fixed by SPEC §4: the primary action goes **last** so it lands
 * bottom-right under the right thumb, and undo goes first so a mis-tap on the far side of the
 * screen cannot destroy anything.
 */
export function row(children: readonly HTMLElement[], doc: Document = document): HTMLElement {
  const node = el('div', 'row', doc);
  node.append(...children);
  return node;
}

/** `m:ss`, tabular. Rounded up, so the last displayed second is a whole second of play. */
export function formatSeconds(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
